import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Alert,
  AlertTitle,
  Divider,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
} from "chart.js";
import moment from "moment";
import Table from "./Table";
import Options from "./Options";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend,
);

function BloodP(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filterProps, setFilterProps] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // לוגיקת בדיקת חריגות (Alerts)
  const checkBloodpAlert = (bloodP) =>
    bloodP.sys > 180 || bloodP.sys < 90 || bloodP.dia > 110 || bloodP.dia < 40;
  const checkPulseAlert = (bloodP) => bloodP.pulse > 130 || bloodP.pulse < 40;

  // עיבוד נתונים עם useMemo לשיפור ביצועים
  const resultList = useMemo(() => {
    const list = props.selectedPatient
      ? props.bloodPResults?.filter(
          (a) => a.custid == props.selectedPatient.custid,
        )
      : props.bloodPResults || [];

    return list.map((obj) => ({
      ...obj,
      ifAlert: checkBloodpAlert(obj) || checkPulseAlert(obj),
    }));
  }, [props.selectedPatient, props.bloodPResults]);

  const measurmentFiltered = useMemo(() => {
    let filtered = [...resultList];
    if (filterProps?.filterSelected === "lastMonth") {
      filtered = filtered.filter(
        (m) =>
          new Date(m.devdatetime) > moment().subtract(1, "months").toDate(),
      );
    } else if (filterProps?.filterSelected === "betweenDates") {
      filtered = filtered.filter(
        (m) =>
          new Date(m.devdatetime) > filterProps?.fromDate &&
          new Date(m.devdatetime) < filterProps?.untilDate,
      );
    } else if (!filterProps?.filterSelected) {
      filtered = filtered.slice(0, 10);
    }
    return filtered.sort((b, a) => a.internalcounter - b.internalcounter);
  }, [resultList, filterProps]);

  const pages = Math.ceil(measurmentFiltered.length / 10);
  const toShow = measurmentFiltered.slice(
    (currentPage - 1) * 10,
    (currentPage - 1) * 10 + 10,
  );

  // הגדרות המידע לגרף
  const chartData = {
    labels: toShow.map((a) =>
      moment(a.devdatetime).format(isMobile ? "DD/MM" : "DD/MM/YY HH:mm"),
    ),
    datasets: [
      {
        label: "דופק",
        type: "line",
        data: toShow.map((a) => a.pulse),
        borderColor: theme.palette.mode === "dark" ? "#fff" : "#555",
        backgroundColor: theme.palette.primary.main,
        pointBackgroundColor: toShow.map((a) =>
          checkPulseAlert(a) ? "red" : theme.palette.primary.main,
        ),
        fill: false,
        yAxisID: "y",
      },
      {
        label: "לחץ דם",
        type: "bar",
        data: toShow.map((a) => [a.sys, a.dia]),
        backgroundColor: toShow.map((a) =>
          checkBloodpAlert(a)
            ? "rgba(255, 0, 0, 0.6)"
            : "rgba(242, 99, 34, 0.6)",
        ),
        barThickness: isMobile ? 12 : 20,
        yAxisID: "y",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { position: "right", ticks: { color: theme.palette.text.secondary } },
      x: { ticks: { color: theme.palette.text.secondary } },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { family: "Assistant" },
          color: theme.palette.text.primary,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1150px", // הגבלת רוחב למסכים גדולים
        margin: "0 auto",
        p: { xs: 2, md: 4 },
        direction: "rtl",
      }}
    >
      {/* כותרת חיצונית */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          mb: 4,
          color: "text.primary",
          textAlign: "left",
        }}
      >
        מדדי לחץ דם
      </Typography>

      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          backgroundColor: "background.paper",
        }}
      >
        {/* שורת פעולות ופרטי מטופל - עיצוב אחיד */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
            mb: 4,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon sx={{ ml: 1, mr: 0 }} />}
            onClick={() => window.print()}
            className="printerHide"
            sx={{
              borderRadius: "12px",
              height: "56px",
              px: 4,
              fontWeight: 700,
              width: { xs: "100%", md: "auto" },
              boxShadow: `0 4px 12px ${theme.palette.primary.main}33`,
            }}
          >
            הורד / הדפס דו"ח
          </Button>

          <Box
            sx={{
              flex: 1,
              width: "100%",
              p: 1.5,
              px: 2,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "#fcfcfc",
              display: "flex",
              // justifyContent: "center" // אם תרצה שהכל יהיה באמצע השורה
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 4, // רווח בין גוש השם לגוש הת.ז
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                justifyContent: "start",
                flexDirection: "row-reverse",
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {`מטופל\\ת:\u200f`}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {props.selectedPatient?.firstname}{" "}
                {props.selectedPatient?.lastname}
              </Typography>
            </Box>

            {/* קו מפריד דק ביניהם (אופציונלי, נראה מאוד מקצועי) */}
            <Box
              sx={{
                width: "1px",
                height: "16px",
                bgcolor: theme.palette.divider,
              }}
            />

            {/* גוש תעודת זהות */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                flexDirection: "row-reverse",
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {`ת.ז:\u200f`}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {props.selectedPatient?.custid}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* מכתב מקדים */}

        {resultList.length === 0 ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography variant="h6" color="textSecondary">
              אין נתונים להצגה
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {/* פילטרים - תצוגה מוסתרת בהדפסה */}
            <Box className="printerHide">
              <Options updateChart={(p) => setFilterProps(p)} />
            </Box>

            {/* Grid ראשי - טבלה וגרף */}
            <Grid container spacing={4}>
              <Grid item xs={12} lg={7}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: "primary.main",
                    textAlign: "left",
                  }}
                >
                  פירוט המדידות
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Table
                    kind="bloodP"
                    measurment={toShow}
                    pages={pages}
                    currentPage={currentPage}
                    updatePageNumber={(i) => setCurrentPage(i)}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "flex-end",
                  }}
                >
                  גרף
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    height: "450px",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "#fcfcfc",
                  }}
                >
                  <Bar data={chartData} options={chartOptions} />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ mb: 2, lineHeight: 1.8, textAlign: "left" }}>
          מנוי/ה יקר/ה, לפניך תוצאות מדידות לחץ דם אשר בוצעו על ידך באופן עצמאי
          והועברו למוקד שחל. הנתונים מועברים ישירות לתיקך הרפואי. מומלץ למסור
          דו"ח זה לרופא המטפל לצורך מעקב.
        </Typography>
        <Alert
          severity="warning"
          variant="filled"
          sx={{
            borderRadius: 2,
            "& .MuiAlert-message": {
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            },
          }}
        >
          <AlertTitle sx={{ fontWeight: 800 }}>חשוב לזכור</AlertTitle>
          המדידה אינה מהווה תחליף לשיחת חירום. במידה והינך חש/ה ברע, יש ליצור
          קשר טלפוני מיידי עם המוקד.
        </Alert>
      </Box>
      {/* הגדרות הדפסה */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media print {
          .printerHide { display: none !important; }
          body { background: white !important; padding: 0 !important; }
          .MuiPaper-root { box-shadow: none !important; border: none !important; padding: 0 !important; }
          .MuiDivider-root { display: none; }
        }
      `,
        }}
      />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  bloodPResults: state.bloodPResults,
  selectedPatient: state.selectedPatient,
});

export default connect(mapStateToProps)(BloodP);
